import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { DIAMOND_TYPES, STONE_SHAPES } from '@/utils/constants';

export function TaskDiamondsTab({ formData, onChange, canEdit, emptyDiamond, emptyStone }) {
  const addDiamond = () => onChange('diamonds', [...formData.diamonds, { ...emptyDiamond }]);
  const removeDiamond = (i) => onChange('diamonds', formData.diamonds.filter((_, idx) => idx !== i));
  const updateDiamond = (i, field, value) => onChange('diamonds', formData.diamonds.map((d, idx) => idx === i ? { ...d, [field]: value } : d));

  const addOtherStone = () => onChange('other_stones', [...formData.other_stones, { ...emptyStone }]);
  const removeOtherStone = (i) => onChange('other_stones', formData.other_stones.filter((_, idx) => idx !== i));
  const updateOtherStone = (i, field, value) => onChange('other_stones', formData.other_stones.map((s, idx) => idx === i ? { ...s, [field]: value } : s));

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Diamond Summary</h3>
        <div className="grid grid-cols-4 gap-4">
          <div>
            <Label htmlFor="diamond_type">Type</Label>
            <select id="diamond_type" value={formData.diamond_type} onChange={e => onChange('diamond_type', e.target.value)} disabled={!canEdit}
              className="mt-2 w-full h-10 px-3 border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/10">
              <option value="">Select</option>
              {DIAMOND_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div><Label htmlFor="total_diamond_pieces">Total Pieces</Label><Input id="total_diamond_pieces" type="number" value={formData.total_diamond_pieces} onChange={e => onChange('total_diamond_pieces', e.target.value)} disabled={!canEdit} className="mt-2" /></div>
          <div><Label htmlFor="total_diamond_weight_ct">Total Weight (ct)</Label><Input id="total_diamond_weight_ct" type="number" step="0.01" value={formData.total_diamond_weight_ct} onChange={e => onChange('total_diamond_weight_ct', e.target.value)} disabled={!canEdit} className="mt-2" /></div>
          <div><Label htmlFor="diamond_quality_code">Quality</Label><Input id="diamond_quality_code" value={formData.diamond_quality_code} onChange={e => onChange('diamond_quality_code', e.target.value)} placeholder="FG-VS" disabled={!canEdit} className="mt-2" /></div>
        </div>
      </div>

      <div>
        <div className="flex justify-between mb-4">
          <h3 className="text-lg font-semibold">Diamond Breakdown</h3>
          {canEdit && <Button type="button" onClick={addDiamond} size="sm" variant="outline"><Plus size={16} className="mr-2" />Add Diamond</Button>}
        </div>
        {formData.diamonds.length === 0 ? (
          <div className="text-center py-8 border border-dashed rounded-lg text-slate-500">No diamond entries</div>
        ) : (
          <div className="space-y-4">
            {formData.diamonds.map((diamond, idx) => (
              <div key={diamond.id || `diamond-${idx}`} className="p-4 border rounded-lg bg-slate-50">
                <div className="flex justify-between mb-3">
                  <h4 className="font-semibold">Diamond #{idx + 1}</h4>
                  {canEdit && <Button type="button" onClick={() => removeDiamond(idx)} size="sm" variant="ghost" className="text-rose-600"><Trash2 size={16} /></Button>}
                </div>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label className="text-xs">Shape</Label>
                    <select value={diamond.stone_shape} onChange={e => updateDiamond(idx, 'stone_shape', e.target.value)} disabled={!canEdit} className="mt-1 w-full h-9 px-2 text-sm border rounded-md bg-white">
                      {STONE_SHAPES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div><Label className="text-xs">Size (mm)</Label><Input value={diamond.stone_size_mm} onChange={e => updateDiamond(idx, 'stone_size_mm', e.target.value)} disabled={!canEdit} className="mt-1 h-9 text-sm" /></div>
                  <div><Label className="text-xs">Weight (ct)</Label><Input type="number" step="0.001" value={diamond.stone_weight_ct} onChange={e => updateDiamond(idx, 'stone_weight_ct', parseFloat(e.target.value) || 0)} disabled={!canEdit} className="mt-1 h-9 text-sm" /></div>
                  <div><Label className="text-xs">Pieces</Label><Input type="number" value={diamond.pieces} onChange={e => updateDiamond(idx, 'pieces', parseInt(e.target.value) || 0)} disabled={!canEdit} className="mt-1 h-9 text-sm" /></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex justify-between mb-4">
          <h3 className="text-lg font-semibold">Other Stones</h3>
          {canEdit && <Button type="button" onClick={addOtherStone} size="sm" variant="outline"><Plus size={16} className="mr-2" />Add Stone</Button>}
        </div>
        {formData.other_stones.length === 0 ? (
          <div className="text-center py-8 border border-dashed rounded-lg text-slate-500">No other stones</div>
        ) : (
          <div className="space-y-4">
            {formData.other_stones.map((stone, idx) => (
              <div key={stone.id || `stone-${idx}`} className="p-4 border rounded-lg bg-slate-50">
                <div className="flex justify-between mb-3">
                  <h4 className="font-semibold">Stone #{idx + 1}</h4>
                  {canEdit && <Button type="button" onClick={() => removeOtherStone(idx)} size="sm" variant="ghost" className="text-rose-600"><Trash2 size={16} /></Button>}
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label className="text-xs">Name</Label><Input value={stone.stone_name} onChange={e => updateOtherStone(idx, 'stone_name', e.target.value)} placeholder="Ruby, Emerald" disabled={!canEdit} className="mt-1 h-9 text-sm" /></div>
                  <div><Label className="text-xs">Shape</Label><Input value={stone.shape} onChange={e => updateOtherStone(idx, 'shape', e.target.value)} disabled={!canEdit} className="mt-1 h-9 text-sm" /></div>
                  <div><Label className="text-xs">Size (mm)</Label><Input value={stone.size_mm} onChange={e => updateOtherStone(idx, 'size_mm', e.target.value)} disabled={!canEdit} className="mt-1 h-9 text-sm" /></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
